import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import type { QuickFillItem, QuickFillRowMode } from '../../../shared/types';
import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';
import { perf } from '../../../shared/lib/perfLogger';
import {
  buildQuickFillDataAsync,
  buildBuiltDatasetCacheKey,
  buildCheckedPropertiesSignature,
  buildCurrentRevitParameters,
  buildDatasetCacheKey,
  cloneBuiltDatasetCacheEntry,
  cloneQuickFillDataRows,
  quickFillBuiltDatasetCache,
  rememberBuiltDatasetCacheEntryWithPerf,
  type AvailableParameterMeta,
  type PipeFamily,
  type QuickFillDatasetCacheKey,
} from '../lib/quickFillDataTransforms';
import type { PipeElement } from '../lib/quickFillProjectIndexes';
import {
  applyQuickFillParameterBindingKinds,
  type QuickFillConfiguredBindingKind,
} from '../lib/quickFillPageModel';

const EMPTY_PARAMETER_BINDING_KINDS: ReadonlyMap<string, QuickFillConfiguredBindingKind> = new Map();

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useQuickFillDataBuilder(
  pipeFamilies: PipeFamily[],
  pipeElements: PipeElement[],
  availableParameters: AvailableParameterMeta[],
  isInitializing: boolean,
  initializationDoneRef: React.MutableRefObject<boolean>,
  rowMode: QuickFillRowMode = 'familyType',
  allowFamilyTypeFallback = true,
  snapshotShape: QuickFillSnapshotShapeOption = 'familyType',
  datasetSourceKey: string | null = null,
  parameterBindingKinds: ReadonlyMap<string, QuickFillConfiguredBindingKind> = EMPTY_PARAMETER_BINDING_KINDS
) {
  const properties = useQuickFillStore(state => state.properties);
  const extraProperties = useQuickFillStore(state => state.extraProperties);
  const currentQuickFillData = useQuickFillStore(state => state.quickFillData);
  const editedCells = useQuickFillStore(state => state.editedCells);
  const replaceQuickFillDataFromSource = useQuickFillStore(state => state.replaceQuickFillDataFromSource);
  const configuredProperties = useMemo(
    () => applyQuickFillParameterBindingKinds(properties, parameterBindingKinds),
    [parameterBindingKinds, properties]
  );
  const configuredExtraProperties = useMemo(
    () => applyQuickFillParameterBindingKinds(extraProperties, parameterBindingKinds),
    [extraProperties, parameterBindingKinds]
  );

  const hasPipeFamilies = Array.isArray(pipeFamilies) && pipeFamilies.length > 0;
  const hasPipeElements = Array.isArray(pipeElements) && pipeElements.length > 0;
  const effectiveRowMode: QuickFillRowMode =
    allowFamilyTypeFallback && rowMode === 'instance' && !hasPipeElements && hasPipeFamilies
      ? 'familyType'
      : rowMode;

  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState('');
  const dataInitializedRef = useRef(false);
  const prevRevitParametersRef = useRef<Record<string, string>>({});
  const currentQuickFillDataRef = useRef<QuickFillItem[]>([]);
  const datasetCacheRef = useRef<Partial<Record<QuickFillDatasetCacheKey, QuickFillItem[] | null>>>({});
  const previousRowModeRef = useRef<QuickFillRowMode>(effectiveRowMode);
  const previousSnapshotShapeRef = useRef<QuickFillSnapshotShapeOption>(snapshotShape);
  const buildIdRef = useRef(0);
  const editedCellsRef = useRef<Record<string, boolean>>({});

  const onProgress = useCallback((built: number, total: number) => {
    const nextProgress = `Processing rows ${built}/${total}…`;
    setBuildProgress((previous) => previous === nextProgress ? previous : nextProgress);
  }, []);

  useEffect(() => {
    currentQuickFillDataRef.current = Array.isArray(currentQuickFillData) ? currentQuickFillData : [];

    if (!dataInitializedRef.current && Array.isArray(currentQuickFillData) && currentQuickFillData.length > 0) {
      dataInitializedRef.current = true;
    }

    if (
      dataInitializedRef.current &&
      Object.keys(prevRevitParametersRef.current).length === 0 &&
      Array.isArray(configuredProperties) &&
      configuredProperties.length > 0
    ) {
      prevRevitParametersRef.current = buildCurrentRevitParameters(configuredProperties);
    }
  }, [configuredProperties, currentQuickFillData]);

  useEffect(() => {
    editedCellsRef.current = editedCells || {};
  }, [editedCells]);

  useEffect(() => {
    datasetCacheRef.current = {};
  }, [pipeFamilies, pipeElements, availableParameters, configuredProperties, configuredExtraProperties]);

  useEffect(() => {
    const datasetCacheKey = buildDatasetCacheKey(effectiveRowMode, snapshotShape);
    if (
      previousRowModeRef.current !== effectiveRowMode ||
      previousSnapshotShapeRef.current !== snapshotShape
    ) {
      dataInitializedRef.current = false;
      previousRowModeRef.current = effectiveRowMode;
      previousSnapshotShapeRef.current = snapshotShape;

      const cachedRows = datasetCacheRef.current[datasetCacheKey];
      if (cachedRows) {
        dataInitializedRef.current = true;
        unstable_batchedUpdates(() => {
          setIsBuilding(false);
          setBuildProgress('');
          replaceQuickFillDataFromSource(cloneQuickFillDataRows(cachedRows) || []);
        });
        return;
      }
    }

    if (isInitializing || !initializationDoneRef.current) return;
    if (
      effectiveRowMode === 'familyType' &&
      (!pipeFamilies || pipeFamilies.length === 0)
    ) {
      perf.warn('QF-Builder', 'Skipped build because Quick Fill has no pipe families after initialization', {
        pipeFamilies: pipeFamilies?.length ?? 0,
        pipeElements: pipeElements?.length ?? 0,
        availableParameters: availableParameters?.length ?? 0,
      });
      return;
    }
    if (
      effectiveRowMode === 'instance' &&
      (!pipeElements || pipeElements.length === 0)
    ) {
      perf.warn('QF-Builder', 'Skipped build because Quick Fill has no pipe elements for instance mode', {
        pipeFamilies: pipeFamilies?.length ?? 0,
        pipeElements: pipeElements?.length ?? 0,
        availableParameters: availableParameters?.length ?? 0,
      });
      return;
    }
    if (!Array.isArray(configuredProperties)) {
      perf.warn('QF-Builder', 'Skipped build because Quick Fill properties state is not an array');
      return;
    }

    const tCacheLookup = performance.now();
    const builtDatasetCacheKey = buildBuiltDatasetCacheKey(
      datasetSourceKey,
      effectiveRowMode,
      snapshotShape,
      configuredProperties,
      configuredExtraProperties
    );
    const builtDatasetCacheEntry = cloneBuiltDatasetCacheEntry(
      builtDatasetCacheKey ? quickFillBuiltDatasetCache.get(builtDatasetCacheKey) : undefined
    );
    const cacheLookupDetails = {
      hit: Boolean(builtDatasetCacheEntry),
      hasSourceKey: Boolean(datasetSourceKey),
      cacheSize: quickFillBuiltDatasetCache.size,
      rowMode: effectiveRowMode,
      snapshotShape,
      families: pipeFamilies.length,
      elements: pipeElements?.length ?? 0,
      currentRows: currentQuickFillDataRef.current.length,
      checkedColumns: buildCheckedPropertiesSignature(
        configuredProperties,
        configuredExtraProperties
      ).split('|').filter(Boolean).length,
    };
    perf.log('QF-Builder', 'Built dataset cache lookup', cacheLookupDetails);
    perf.slow('QF-Builder', 'built dataset cache lookup', tCacheLookup, 10, cacheLookupDetails);
    if (builtDatasetCacheEntry) {
      const tCacheRestore = performance.now();
      datasetCacheRef.current[datasetCacheKey] = cloneQuickFillDataRows(builtDatasetCacheEntry.rows);
      unstable_batchedUpdates(() => {
        setIsBuilding(false);
        setBuildProgress('');
        replaceQuickFillDataFromSource(builtDatasetCacheEntry.rows);
      });
      perf.slow('QF-Builder', 'restore built dataset into store', tCacheRestore, 25, {
        rows: builtDatasetCacheEntry.rows.length,
        rowMode: effectiveRowMode,
        snapshotShape,
      });
      prevRevitParametersRef.current = builtDatasetCacheEntry.currentRevitParameters;
      dataInitializedRef.current = true;
      perf.log('QF-Builder', 'Restored built Quick Fill dataset from cache', {
        rows: builtDatasetCacheEntry.rows.length,
        rowMode: effectiveRowMode,
        snapshotShape,
      });
      return;
    }

    setIsBuilding(true);
    setBuildProgress('Preparing…');
    const currentBuildId = ++buildIdRef.current;
    const scheduledAt = performance.now();

    // Debounce 150ms — wait for React state to settle after rapid re-renders
    const timerId = setTimeout(() => {
      const isStillValid = () => buildIdRef.current === currentBuildId;
      if (!isStillValid()) return;
      perf.slow('QF-Builder', 'debounce timer delay before build', scheduledAt, 180, {
        targetDelayMs: 150,
        rowMode: effectiveRowMode,
        snapshotShape,
        families: pipeFamilies.length,
        elements: pipeElements?.length ?? 0,
      });

      perf.log('QF-Builder', 'Async build START', {
        families: pipeFamilies.length,
        elements: pipeElements?.length ?? 0,
        requestedRowMode: rowMode,
        rowMode: effectiveRowMode,
        snapshotShape,
      });

      const tAsyncBuild = performance.now();
      let didSettleBuildUi = false;
      void buildQuickFillDataAsync(
          pipeFamilies,
          pipeElements,
          availableParameters,
          configuredProperties,
          configuredExtraProperties,
          currentQuickFillDataRef.current,
          editedCellsRef.current,
          dataInitializedRef.current,
          effectiveRowMode,
          prevRevitParametersRef.current,
          isStillValid,
          onProgress,
          snapshotShape,
        ).then(result => {
          if (!isStillValid()) return;
          perf.slow('QF-Builder', 'buildDataAsync resolved', tAsyncBuild, effectiveRowMode === 'instance' ? 900 : 320, {
            rows: result.data?.length ?? currentQuickFillDataRef.current.length,
            changed: Boolean(result.data),
            rowMode: effectiveRowMode,
            snapshotShape,
          });
          prevRevitParametersRef.current = result.currentRevitParameters;
          if (result.data) {
            datasetCacheRef.current[datasetCacheKey] = cloneQuickFillDataRows(result.data);
            perf.log('QF-Builder', 'Applying build result to Quick Fill store', {
              rows: result.data.length,
            });
            const tStoreReplace = performance.now();
            unstable_batchedUpdates(() => {
              setIsBuilding(false);
              setBuildProgress('');
              replaceQuickFillDataFromSource(result.data || []);
            });
            didSettleBuildUi = true;
            perf.slow('QF-Builder', 'replace Quick Fill store from build result', tStoreReplace, 30, {
              rows: result.data.length,
              rowMode: effectiveRowMode,
              snapshotShape,
            });
            rememberBuiltDatasetCacheEntryWithPerf(
              builtDatasetCacheKey,
              result.data,
              result.currentRevitParameters,
              { hasSourceKey: Boolean(datasetSourceKey) }
            );
            if (!dataInitializedRef.current) dataInitializedRef.current = true;
          } else {
            rememberBuiltDatasetCacheEntryWithPerf(
              builtDatasetCacheKey,
              currentQuickFillDataRef.current,
              result.currentRevitParameters,
              { hasSourceKey: Boolean(datasetSourceKey) },
              'remember no-op built dataset cache entry'
            );
            perf.log('QF-Builder', 'Build completed without any store update');
          }
        }).catch((error) => {
          if (!isStillValid()) return;
          perf.warn('QF-Builder', 'Async build failed', {
            rowMode: effectiveRowMode,
            error: error instanceof Error ? error.message : String(error),
          });
          setIsBuilding(false);
          setBuildProgress('');
        }).finally(() => {
          if (!isStillValid()) return;
          perf.slow('QF-Builder', 'async build total including debounce', scheduledAt, effectiveRowMode === 'instance' ? 1500 : 750, {
            rowMode: effectiveRowMode,
            snapshotShape,
            families: pipeFamilies.length,
            elements: pipeElements?.length ?? 0,
          });
          if (!didSettleBuildUi) {
            setIsBuilding(false);
            setBuildProgress('');
          }
        });
    }, 150);

    return () => {
      const nextBuildId = buildIdRef.current + 1;
      clearTimeout(timerId);
      buildIdRef.current = nextBuildId;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredProperties, configuredExtraProperties, pipeFamilies, pipeElements, availableParameters, replaceQuickFillDataFromSource, isInitializing, effectiveRowMode, rowMode, allowFamilyTypeFallback, snapshotShape, datasetSourceKey]);

  return { isBuilding, buildProgress };
}
